<script>
import TwContainerFullWidth from '../../../components/generic/tw-container-full-width.vue'
import TwCard from '@/components/generic/tw-card.vue'
import TwButton from '@/components/generic/tw-button.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import fundraiserPublicFetchMixin from '@/mixins/fundraiserPublicFetchMixin.js'
import TwTransitionContainer from '@/components/generic/tw-transition-container.vue'
import fundraiserDonateMixin from '@/mixins/fundraiserDonateMixin.js'
import DonateAmountField from '@/components/donations/public/donate-amount-field.vue'
import TwCheckbox from '@/components/generic/forms/tw-checkbox.vue'
import TwValidationErrors from '@/components/generic/tw-validation-errors.vue'
import TwInput from '@/components/generic/forms/tw-input.vue'
import TwTextarea from '@/components/generic/forms/tw-textarea.vue'
import TwTransitionListContainer from '@/components/generic/tw-transition-list-container.vue'
import FundraiserDonationAppFeeCard from '@/components/fundraisers/public/fundraiser-donation-app-fee-card.vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import DonateTakeCareOfFeesCard from '../../../components/donations/public/donate-take-care-of-fees-card.vue'

export default {
  name: 'FundraiserDonatePage',
  components: { DonateTakeCareOfFeesCard, FundraiserDonationAppFeeCard, TwTransitionListContainer, TwTextarea, TwInput, TwValidationErrors, TwCheckbox, DonateAmountField, TwTransitionContainer, TwButton, XMarkIcon },
  mixins:[fundraiserPublicFetchMixin, fundraiserDonateMixin],
  async mounted() {
    await this.fetchFundraiser()
    if(this.fundraiser && this.fundraiser.settings && this.fundraiser.settings.force_donating_to_fundraiser_pages_only) {
      this.$notify('Please select a supporter you want to donate to first', 'warning')
      this.$router.push({ name: 'FundraiserPublicPage', params: { fundraiserId: this.fundraiser.id, slug: this.fundraiser.slug } })
    }
    this.$refs.nameField.$refs.inputfield.focus()
    this.$refs.nameField.Validated = false
    if(this.fundraiser && this.fundraiser.settings && this.fundraiser.settings.allow_donors_to_absorb_credit_card_fees) {
      this.DonationTakeCareOfFees = true
    }
  }
}
</script>

<template>
  <tw-transition-container>
    <div v-if="fundraiser" class="h-100">
      <div v-if="fundraiser" class="w-full bg-cover bg-center bg-no-repeat fixed h-screen bg-overlay z-0" :style="`background-image: url('${fundraiser.cover_image_url}');`" />
      <div class="z-10 flex justify-center sm:p-6 relative z-1">
        <tw-transition-list-container>
          <div class="w-full sm:max-w-lg">
            <div v-if="fundraiser" class="rounded-none sm:rounded-2xl bg-white shadow-sm relative p-6 md:p-8 min-h-screen sm:min-h-max">
              <tw-button variant="link" class="absolute right-4 top-4" :to="{ name: 'FundraiserPublicPage', params: { fundraiserId: fundraiser.id, slug: fundraiser.slug} }">
                <XMarkIcon class="size-7" />
              </tw-button>
              <div class="flex gap-3 flex-col text-center mt-8 mb-4">
                <h1>
                  {{ fundraiser.title }}
                </h1>
                <h6> by <span>{{ fundraiser.cached.beneficiary_name }}</span> </h6>
              </div>
              <div v-if="!DonationCompleted">
                <div>
                  <div v-if="!DonationGatewayActive && !DonationCompleted">
                    <donate-amount-field ref="donationField" v-model="DonationAmount" size="lg" :currency="donationCurrency" />
                    <donate-take-care-of-fees-card v-if="fundraiser.settings.allow_donors_to_absorb_credit_card_fees" v-model="DonationTakeCareOfFees" class="mt-2" :currency="donationCurrency" :donation-stripe-fee-offset-amount="donationStripeFeeOffsetAmount" />
                    <div v-if="fundraiser.settings.enable_recurring_donations" class="mt-2">
                      <tw-checkbox v-model="DonationRecurring" label="Make this a monthly donation" />
                      <tw-transition-container>
                        <div v-if="DonationRecurring && recurrencesAvailable.length > 1" class="grid grid-cols-3 gap-2">
                          <tw-button v-for="recurrence in recurrencesAvailable" :key="'recurrences-key-' + recurrence" :fa-icon="DonationRecurrences === recurrence? 'check':null" block :variant="DonationRecurrences === recurrence? 'success':'secondary'" size="sm" @click="DonationRecurrences = recurrence">
                            {{ recurrenceLabel(recurrence) }}
                          </tw-button>
                        </div>
                      </tw-transition-container>
                    </div>

                    <div v-if="fundraiser.cached.donation_highest_amount > 0" class="mt-6 text-center text-sm bg-gray-50 p-4 rounded-lg">
                      A donation of <span class="text-amber-500 underline cursor-pointer font-medium" @click="DonationAmount = fundraiser.cached.donation_highest_amount + 1">{{ $filters.formatCurrency(fundraiser.cached.donation_highest_amount, donationCurrency) }} or more</span> would make you the top giver so far.
                    </div>
                    <div class="flex flex-col gap-2">
                      <tw-input ref="nameField" v-model="DonationName" required class="mt-4 mb-1" label="Name" placeholder="Enter your name (required)" />
                      <tw-checkbox v-model="DonationAnonymousName">
                        Keep my name hidden from public view
                      </tw-checkbox>
                      <tw-checkbox v-model="DonationAnonymousAmount">
                        Keep my amount hidden from public view
                      </tw-checkbox>
                      <tw-checkbox v-model="DonationShowCommentForm">
                        Leave a public message
                      </tw-checkbox>
                      <div v-if="DonationShowCommentForm" class="mt-1">
                        <tw-textarea v-model="DonationComment" class="w-full" rows="3" placeholder="Enter your message" />
                      </div>
                    </div>
                    <fundraiser-donation-app-fee-card v-if="fundraiser && !fundraiser.settings.app_fee_included" v-model="DonationAppFeeAmount" :currency="donationCurrency" :donation-amount="DonationAmount" class="mt-5 mb-3" />
                    <div class="mt-8 flex justify-between items-center">
                      <h5>
                        Donation Total:
                      </h5>
                      <h5 class="text-right">
                        {{ $filters.formatCurrency(donationTotalAmount, donationCurrency) }}
                      </h5>
                    </div>
                    <div class="mt-3">
                      <tw-button block variant="amber" size="lg" :disabled="donateButtonDisabled" @click="startDonation">
                        {{ continueButtonLabel }}
                      </tw-button>
                    </div>
                  </div>
                </div>
              </div>
              <tw-validation-errors :validation-result-error="ValidationErrorMessage" />
              <tw-transition-list-container>
                <div v-if="DonationGatewayActive" id="checkout" />
                <!-- TODO: Should this be a back button? It takes the user back to the previous step but then the continue button does not work after that. This seams like a glitch?
                  <tw-button v-if="DonationGatewayActive" class="mt-2" size="lg" block variant="secondary" @click="cancelDonation">
                    Cancel
                  </tw-button> -->
              </tw-transition-list-container>
              <div class="mt-4 text-xs text-center">
                <i class="pi pi-lock mr-1" />Your donation is safe and secure. All proceeds of your donation will go directly to <span class="font-medium">{{ fundraiser.cached.beneficiary_name }}</span>.
              </div>
            </div>
          </div>
        </tw-transition-list-container>
      </div>
    </div>
  </tw-transition-container>
</template>

<style scoped>

</style>
